import React, { useCallback, useState } from "react";
import { Plus } from "lucide-react";
import { createCoupon, listCoupons, updateCoupon } from "../../api/billing";
import { formatDateTime } from "../../shared/utils/format";
import { DataTable, LoadState, type Column } from "../UserDetails/ui";
import type { Coupon } from "./types";
import {
  Chip,
  ErrorText,
  Field,
  Modal,
  SaveButton,
  Toggle,
  fromLocalInput,
  inputClass,
  linkButtonClass,
  primaryButtonClass,
  secondaryButtonClass,
  toLocalInput,
  useAction,
  useLoad,
} from "./ui";

const CODE_PATTERN = /^[A-Z0-9_-]{3,40}$/;

const optionalInt = (value: string) => (value.trim() ? Number.parseInt(value, 10) : null);
const optionalText = (value: string) => value.trim() || null;

/** Create a coupon, or edit the fields the server allows changing on an existing one. */
const CouponFormModal: React.FC<Readonly<{ coupon: Coupon | null; onClose: () => void; onSaved: () => void }>> = ({
  coupon,
  onClose,
  onSaved,
}) => {
  const isEdit = coupon !== null;
  const [code, setCode] = useState(coupon?.code ?? "");
  const [autoApply, setAutoApply] = useState(coupon?.autoApply ?? false);
  const [description, setDescription] = useState(coupon?.description ?? "");
  const [discountPercent, setDiscountPercent] = useState(coupon ? String(Number(coupon.discountPercent)) : "20");
  const [duration, setDuration] = useState<Coupon["duration"]>(coupon?.duration ?? "FIRST_MONTH");
  const [maxRedemptions, setMaxRedemptions] = useState(coupon?.maxRedemptions ? String(coupon.maxRedemptions) : "");
  const [perUserLimit, setPerUserLimit] = useState(String(coupon?.perUserLimit ?? 1));
  const [firstPaidUpgradeOnly, setFirstPaidUpgradeOnly] = useState(coupon?.firstPaidUpgradeOnly ?? false);
  const [startsAt, setStartsAt] = useState(toLocalInput(coupon?.startsAt));
  const [expiresAt, setExpiresAt] = useState(toLocalInput(coupon?.expiresAt));
  const [offerCard, setOfferCard] = useState(coupon?.razorpayOfferIdCard ?? "");
  const [offerUpi, setOfferUpi] = useState(coupon?.razorpayOfferIdUpi ?? "");
  const [offerAny, setOfferAny] = useState(coupon?.razorpayOfferId ?? "");
  const [isActive, setIsActive] = useState(coupon?.isActive ?? true);
  const { busy, error, setError, run } = useAction();

  const save = async () => {
    const pct = Number(discountPercent);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) return setError("Discount must be between 1 and 100.");
    if (!isEdit && !autoApply && !CODE_PATTERN.test(code)) return setError("Code: 3-40 capital letters, digits, _ or -.");
    if (!offerCard.trim() && !offerUpi.trim() && !offerAny.trim()) {
      return setError("Add at least one Razorpay Offer id — without it the coupon can't apply at checkout.");
    }
    const shared = {
      description: optionalText(description),
      discountPercent: pct,
      maxRedemptions: optionalInt(maxRedemptions),
      expiresAt: fromLocalInput(expiresAt),
      razorpayOfferIdCard: optionalText(offerCard),
      razorpayOfferIdUpi: optionalText(offerUpi),
      razorpayOfferId: optionalText(offerAny),
      isActive,
    };
    const saved = await run(() =>
      isEdit
        ? updateCoupon(coupon.id, shared)
        : createCoupon({
            ...shared,
            code: autoApply ? null : code,
            autoApply,
            duration,
            perUserLimit: optionalInt(perUserLimit) ?? 1,
            firstPaidUpgradeOnly,
            startsAt: fromLocalInput(startsAt),
          })
    );
    if (saved) onSaved();
  };

  return (
    <Modal
      title={isEdit ? `Edit coupon ${coupon.code ?? "(auto-apply)"}` : "New coupon"}
      onClose={onClose}
      busy={busy}
      width="xl"
      footer={
        <>
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <SaveButton busy={busy} onClick={save}>
            {isEdit ? "Save coupon" : "Create coupon"}
          </SaveButton>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="Code" hint={autoApply ? "Auto-apply coupons have no code." : undefined}>
            <input
              className={inputClass}
              value={code}
              disabled={isEdit || autoApply}
              onChange={(e) => setCode(e.target.value.trim().toUpperCase())}
              placeholder="LAUNCH20"
            />
          </Field>
          <Field label="Discount %">
            <input type="number" min={1} max={100} className={inputClass} value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} />
          </Field>
          <Field label="Applies to">
            <select className={inputClass} value={duration} disabled={isEdit} onChange={(e) => setDuration(e.target.value as Coupon["duration"])}>
              <option value="FIRST_MONTH">First payment only</option>
              <option value="FOREVER">Every payment</option>
            </select>
          </Field>
          <Field label="Description (internal)" className="md:col-span-3">
            <input className={inputClass} value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field label="Max uses in total">
            <input type="number" min={1} className={inputClass} value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} />
          </Field>
          <Field label="Uses per user">
            <input type="number" min={1} className={inputClass} value={perUserLimit} disabled={isEdit} onChange={(e) => setPerUserLimit(e.target.value)} />
          </Field>
          <div />
          <Field label="Starts">
            <input type="datetime-local" className={inputClass} value={startsAt} disabled={isEdit} onChange={(e) => setStartsAt(e.target.value)} />
          </Field>
          <Field label="Expires">
            <input type="datetime-local" className={inputClass} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </Field>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Razorpay Offer ids</p>
          <p className="text-xs text-gray-400">Create the Offer in the Razorpay dashboard first, then paste its id here.</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field label="Card">
              <input className={inputClass} value={offerCard} onChange={(e) => setOfferCard(e.target.value)} placeholder="offer_..." />
            </Field>
            <Field label="UPI">
              <input className={inputClass} value={offerUpi} onChange={(e) => setOfferUpi(e.target.value)} placeholder="offer_..." />
            </Field>
            <Field label="Any method (fallback)">
              <input className={inputClass} value={offerAny} onChange={(e) => setOfferAny(e.target.value)} placeholder="offer_..." />
            </Field>
          </div>
        </div>

        <div className="flex flex-wrap gap-6 text-sm text-gray-700">
          {!isEdit && (
            <>
              <Toggle checked={autoApply} onChange={setAutoApply} label="Auto-apply" text="Auto-apply (no code needed)" />
              <Toggle
                checked={firstPaidUpgradeOnly}
                onChange={setFirstPaidUpgradeOnly}
                label="First paid upgrade only"
                text="First paid upgrade only"
              />
            </>
          )}
          <Toggle checked={isActive} onChange={setIsActive} label="Coupon active" text="Active" />
        </div>
        {isEdit && <p className="text-xs text-gray-400">Code, type, per-user limit and start date can't be changed after creation.</p>}
        <ErrorText message={error} />
      </div>
    </Modal>
  );
};

/** Discount coupons (DrawPost, Razorpay Offers). */
export const CouponsTab: React.FC = () => {
  const load = useCallback(() => listCoupons(), []);
  const { data, loading, error, reload } = useLoad(load);
  const [editing, setEditing] = useState<Coupon | "new" | null>(null);
  const toggle = useAction();

  const setActive = async (coupon: Coupon, isActive: boolean) => {
    const done = await toggle.run(() => updateCoupon(coupon.id, { isActive }));
    if (done) await reload();
  };

  const columns: Column<Coupon>[] = [
    {
      header: "Code",
      render: (c) => (c.code ? <span className="font-mono font-medium">{c.code}</span> : <Chip tone="violet">auto-apply</Chip>),
    },
    { header: "Discount", render: (c) => `${Number(c.discountPercent)}% · ${c.duration === "FOREVER" ? "every payment" : "first payment"}` },
    { header: "Used", render: (c) => (c.maxRedemptions ? `${c.timesRedeemed} / ${c.maxRedemptions}` : `${c.timesRedeemed}`) },
    { header: "Expires", render: (c) => formatDateTime(c.expiresAt), className: "whitespace-nowrap" },
    {
      header: "Active",
      render: (c) => <Toggle checked={c.isActive} disabled={toggle.busy} label={`Coupon ${c.id} active`} onChange={(v) => void setActive(c, v)} />,
    },
    {
      header: "",
      render: (c) => (
        <button type="button" className={linkButtonClass} onClick={() => setEditing(c)}>
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-500">Coupons discount through Razorpay Offers and apply to DrawPost checkouts only.</p>
        <button type="button" className={primaryButtonClass} onClick={() => setEditing("new")}>
          <Plus className="w-4 h-4" /> New coupon
        </button>
      </div>
      {toggle.error && <ErrorText message={toggle.error} />}
      <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        {(loading && !data) || error ? (
          <LoadState loading={loading && !data} error={error} onRetry={reload} />
        ) : (
          <DataTable columns={columns} rows={data ?? []} emptyText="No coupons yet." />
        )}
      </div>
      {editing && (
        <CouponFormModal
          coupon={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
};
